import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCasesFromConversationsJson,
  buildPreviewPairsFromMessages,
  buildTestPlanCasesFromImportText,
  generateUniqueCaseCode,
  normalizeCaseForDraft,
  serializeTestPlanCasesToImportJson,
} from './buildTestPlanCasesFromImport';
import { moveListItem } from './reorderListItem';

const createEmptyCase = (name = '') => ({
  name,
  messages: [''],
  is_skipped: false,
  annotation: '',
});

const generateDashboardPlanName = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `EVAL${datePart}${timePart}`;
};

const createEmptyDraft = (clid) => ({
  clid,
  name: generateDashboardPlanName(),
  description: '',
  cases: [createEmptyCase(generateUniqueCaseCode(clid, []))],
});

const createShortCloneSuffix = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 4);
  }
  // Fallback for environments without crypto.randomUUID
  return Math.random().toString(16).slice(2, 6).padEnd(4, '0');
};

/** True when this row is only the default placeholder (auto case code, no real messages). */
const isEmptyDraftCase = (caseItem, clinicId) => {
  if (!caseItem) return true;
  const messages = Array.isArray(caseItem.messages) ? caseItem.messages : [];
  const hasMessage = messages.some((message) => String(message || '').trim().length > 0);
  if (hasMessage) return false;
  const name = String(caseItem.name || '').trim();
  if (!name) return true;
  const prefix = String(clinicId || 'CASE');
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}\\.\\d+$`).test(name);
};

const normalizeCaseWithUniqueCode = (caseItem, clinicId, existingCases) => ({
  name: generateUniqueCaseCode(clinicId, existingCases),
  messages: Array.isArray(caseItem?.messages) && caseItem.messages.length > 0 ? [...caseItem.messages] : [''],
  is_skipped: !!caseItem?.is_skipped,
  annotation: caseItem?.annotation || '',
  preview_pairs: Array.isArray(caseItem?.preview_pairs) ? [...caseItem.preview_pairs] : [],
});

// Build a run-report object from the current draft using each message's
// expected_result as the assistant answer (no agent run). Mirrors the shape the
// runner produces so ApiTestRunReportModal can render and export it directly.
const buildExpectedReport = (draftPlan, planId, clid) => {
  const cases = (draftPlan?.cases || []).map((c, idx) => {
    const messages = Array.isArray(c?.messages) ? c.messages : [];
    const pairs = Array.isArray(c?.preview_pairs) ? c.preview_pairs : [];
    const exchanges = messages
      .map((m, i) => ({
        message: String(m || '').trim(),
        answer: String(pairs[i]?.expected_result || ''),
      }))
      .filter((ex) => ex.message.length > 0);
    return {
      case_index: idx,
      name: c?.name || `Case ${idx + 1}`,
      session_id: null,
      status: c?.is_skipped ? 'skipped' : 'completed',
      error: null,
      exchanges,
    };
  });
  const now = new Date().toISOString();
  return {
    plan_id: planId || null,
    plan_name: draftPlan?.name || 'Plan',
    clid: clid || null,
    started_at: now,
    finished_at: now,
    status: 'completed',
    selected_case_indices: cases.map((c) => c.case_index),
    cases,
    is_expected_preview: true,
  };
};

const ApiTestPlanPanel = ({
  isOpen,
  onClose,
  clinicId,
  plans,
  selectedPlanId,
  onSelectPlan,
  onSavePlan,
  onDeletePlan,
  onStopRun,
  onGenerateExpectedReport,
  isRunningPlan,
  runProgressText,
  pendingConversationCase,
  onConsumePendingConversationCase,
  pendingConversationCases,
  onConsumePendingConversationCases,
  pendingFocusPlanCase,
  onConsumePendingFocusPlanCase,
  autoCloseOnSave = false,
}) => {
  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );
  const [draft, setDraft] = useState(() => createEmptyDraft(clinicId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isCloningDraft, setIsCloningDraft] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [stagedConversationCase, setStagedConversationCase] = useState(null);
  const [stagedConversationCases, setStagedConversationCases] = useState([]);
  const [expectedResultExpanded, setExpectedResultExpanded] = useState({});
  const [allExpectedExpanded, setAllExpectedExpanded] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const importFileInputRef = useRef(null);
  const jsonEditFileInputRef = useRef(null);
  const [jsonEditOpen, setJsonEditOpen] = useState(false);
  const [jsonEditText, setJsonEditText] = useState('');
  const [jsonEditStatus, setJsonEditStatus] = useState('');
  const caseRowRefs = useRef({});
  const [highlightedCaseIdx, setHighlightedCaseIdx] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    const applyStagedCase = (baseCases) => {
      const normalizedBase = Array.isArray(baseCases) ? baseCases : [];
      const stagedList = [];
      if (stagedConversationCase) stagedList.push(stagedConversationCase);
      if (Array.isArray(stagedConversationCases) && stagedConversationCases.length > 0) {
        stagedList.push(...stagedConversationCases);
      }
      if (stagedList.length === 0) return normalizedBase;

      const solePlaceholder = normalizedBase.length === 1
        && isEmptyDraftCase(normalizedBase[0], clinicId);
      let casesForAccumulation = solePlaceholder ? [] : normalizedBase;
      const appended = stagedList.map((staged) => normalizeCaseForDraft(
        normalizeCaseWithUniqueCode(staged, clinicId, casesForAccumulation)
      ));
      appended.forEach((caseItem) => { casesForAccumulation = [...casesForAccumulation, caseItem]; });

      if (solePlaceholder) return appended;
      return [...normalizedBase, ...appended];
    };

    if (selectedPlan) {
      setIsCloningDraft(false);
      const mappedCases = Array.isArray(selectedPlan.cases) && selectedPlan.cases.length > 0
        ? selectedPlan.cases.map((c) => ({
          name: c.name || '',
          messages: Array.isArray(c.messages) && c.messages.length > 0 ? c.messages : [''],
          is_skipped: !!c.is_skipped,
          annotation: c.annotation || '',
          preview_pairs: Array.isArray(c.preview_pairs) ? c.preview_pairs : [],
        })).map(normalizeCaseForDraft)
        : [normalizeCaseForDraft(createEmptyCase(generateUniqueCaseCode(clinicId, [])))];
      setDraft({
        clid: clinicId,
        name: selectedPlan.name || '',
        description: selectedPlan.description || '',
        cases: applyStagedCase(mappedCases),
      });
      setError(null);
      setSaveSuccess(false);
      return;
    }
    if (isCloningDraft) {
      setError(null);
      return;
    }
    const nextDraft = createEmptyDraft(clinicId);
    setDraft({
      ...nextDraft,
      cases: applyStagedCase(nextDraft.cases.map(normalizeCaseForDraft)),
    });
    setError(null);
    setSaveSuccess(false);
  }, [selectedPlan, clinicId, isCloningDraft, stagedConversationCase, stagedConversationCases]);

  useEffect(() => {
    if (!saveSuccess) return undefined;
    const timer = window.setTimeout(() => setSaveSuccess(false), 5000);
    return () => window.clearTimeout(timer);
  }, [saveSuccess]);

  useEffect(() => {
    if (!isOpen || !pendingConversationCase) return;
    setStagedConversationCase({
      messages: Array.isArray(pendingConversationCase.messages) ? [...pendingConversationCase.messages] : [''],
      is_skipped: !!pendingConversationCase.is_skipped,
      annotation: pendingConversationCase.annotation || '',
      preview_pairs: Array.isArray(pendingConversationCase.preview_pairs) ? [...pendingConversationCase.preview_pairs] : [],
    });
    setDraft((prev) => {
      const shouldReplaceEmptyInitialCase = prev.cases.length === 1 && isEmptyDraftCase(prev.cases[0], clinicId);
      const casesForUniqueCode = shouldReplaceEmptyInitialCase ? [] : prev.cases;
      const normalizedCase = normalizeCaseWithUniqueCode(
        pendingConversationCase,
        clinicId,
        casesForUniqueCode
      );
      return {
        ...prev,
        cases: shouldReplaceEmptyInitialCase
          ? [normalizeCaseForDraft(normalizedCase)]
          : [...prev.cases, normalizeCaseForDraft(normalizedCase)],
      };
    });
    setSaveSuccess(false);
    setError(null);
    onConsumePendingConversationCase?.();
  }, [isOpen, pendingConversationCase, onConsumePendingConversationCase, clinicId]);

  useEffect(() => {
    if (!isOpen || !Array.isArray(pendingConversationCases) || pendingConversationCases.length === 0) return;
    const normalizedPending = pendingConversationCases.map((pendingCase) => ({
      messages: Array.isArray(pendingCase.messages) ? [...pendingCase.messages] : [''],
      is_skipped: !!pendingCase.is_skipped,
      annotation: pendingCase.annotation || '',
      preview_pairs: Array.isArray(pendingCase.preview_pairs) ? [...pendingCase.preview_pairs] : [],
    }));
    setStagedConversationCases(normalizedPending);
    setDraft((prev) => {
      const shouldReplaceEmptyInitialCase = prev.cases.length === 1 && isEmptyDraftCase(prev.cases[0], clinicId);
      let accumulated = shouldReplaceEmptyInitialCase ? [] : [...prev.cases];
      const appended = normalizedPending.map((pendingCase) => {
        const normalizedCase = normalizeCaseForDraft(
          normalizeCaseWithUniqueCode(pendingCase, clinicId, accumulated)
        );
        accumulated = [...accumulated, normalizedCase];
        return normalizedCase;
      });
      return {
        ...prev,
        cases: shouldReplaceEmptyInitialCase ? appended : [...prev.cases, ...appended],
      };
    });
    setSaveSuccess(false);
    setError(null);
    onConsumePendingConversationCases?.();
  }, [isOpen, pendingConversationCases, onConsumePendingConversationCases, clinicId]);

  useEffect(() => {
    if (isOpen) return;
    setStagedConversationCase(null);
    setStagedConversationCases([]);
    setExpectedResultExpanded({});
    setAllExpectedExpanded(false);
    setImportStatus('');
    setJsonEditOpen(false);
    setJsonEditText('');
    setJsonEditStatus('');
  }, [isOpen]);

  useEffect(() => {
    if (!importStatus) return undefined;
    const timer = window.setTimeout(() => setImportStatus(''), 5000);
    return () => window.clearTimeout(timer);
  }, [importStatus]);

  useEffect(() => {
    if (!jsonEditStatus) return undefined;
    const timer = window.setTimeout(() => setJsonEditStatus(''), 5000);
    return () => window.clearTimeout(timer);
  }, [jsonEditStatus]);

  const handleImportFromJsonClick = () => {
    importFileInputRef.current?.click();
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result || '';
        const newCases = buildTestPlanCasesFromImportText(text, file.name, clinicId, draft.cases);
        const formatLabel = String(file.name || '').toLowerCase().endsWith('.csv') ? 'CSV' : 'JSON';
        setDraft((prev) => ({ ...prev, cases: [...prev.cases, ...newCases] }));
        setImportStatus(`Imported ${newCases.length} case${newCases.length === 1 ? '' : 's'} from ${formatLabel}.`);
        setError(null);
        setSaveSuccess(false);
      } catch (err) {
        setError(err?.message || 'Failed to import test cases from file.');
        setImportStatus('');
      }
    };
    reader.onerror = () => {
      setError('Failed to read the selected file.');
      setImportStatus('');
    };
    reader.readAsText(file);
  };

  const downloadJsonFile = (text, filename) => {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCasesJson = () => {
    try {
      const payload = serializeTestPlanCasesToImportJson(draft.cases);
      const text = JSON.stringify(payload, null, 2);
      setJsonEditText(text);
      setJsonEditOpen(true);
      const safeName = String(draft.name || 'plan').replace(/[^\w.-]+/g, '_').slice(0, 64);
      downloadJsonFile(text, `test_plan_${clinicId || 'clinic'}_${safeName}.json`);
      setJsonEditStatus(`Exported ${payload.length} case${payload.length === 1 ? '' : 's'} to JSON.`);
      setError(null);
      console.log('[ApiTestPlanPanel] export cases JSON ok', payload.length);
    } catch (err) {
      console.error('[ApiTestPlanPanel] export cases JSON failed', err);
      setError(err?.message || 'Failed to export cases JSON.');
      setJsonEditStatus('');
    }
  };

  const handleJsonEditUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result || 'null');
        setJsonEditText(JSON.stringify(parsed, null, 2));
        setJsonEditOpen(true);
        setJsonEditStatus(`Loaded ${file.name}`);
        setError(null);
      } catch (err) {
        setError('Invalid JSON file.');
        setJsonEditStatus('');
      }
    };
    reader.onerror = () => {
      setError('Failed to read the selected file.');
      setJsonEditStatus('');
    };
    reader.readAsText(file);
  };

  const handleApplyJsonToDraft = () => {
    try {
      const parsed = JSON.parse(jsonEditText || 'null');
      const nextCases = buildCasesFromConversationsJson(parsed, clinicId, []);
      setDraft((prev) => ({ ...prev, cases: nextCases }));
      setJsonEditStatus(`Applied ${nextCases.length} case${nextCases.length === 1 ? '' : 's'} to draft.`);
      setError(null);
      setSaveSuccess(false);
      console.log('[ApiTestPlanPanel] apply cases JSON ok', nextCases.length);
    } catch (err) {
      console.error('[ApiTestPlanPanel] apply cases JSON failed', err);
      setError(err?.message || 'Failed to apply JSON to draft.');
      setJsonEditStatus('');
    }
  };

  const handleToggleAllExpected = () => {
    const nextAllExpanded = !allExpectedExpanded;
    setAllExpectedExpanded(nextAllExpanded);
    const nextMap = {};
    draft.cases.forEach((_, idx) => {
      nextMap[idx] = nextAllExpanded;
    });
    setExpectedResultExpanded(nextMap);
  };

  const updateCase = (caseIdx, updater) => {
    setDraft((prev) => ({
      ...prev,
      cases: prev.cases.map((c, idx) => (idx === caseIdx ? updater(c) : c)),
    }));
  };

  const moveCaseMessage = (caseIdx, msgIdx, delta) => {
    updateCase(caseIdx, (c) => {
      const nextMessages = moveListItem(c.messages, msgIdx, delta);
      if (nextMessages === c.messages) return c;
      const nextPairs = moveListItem(
        buildPreviewPairsFromMessages(c.messages, c.preview_pairs || []),
        msgIdx,
        delta
      );
      return {
        ...c,
        messages: nextMessages,
        preview_pairs: nextPairs,
      };
    });
    setSaveSuccess(false);
  };

  useEffect(() => {
    if (!isOpen || !pendingFocusPlanCase) return undefined;
    const caseIdx = pendingFocusPlanCase.caseIndex;
    if (!Number.isInteger(caseIdx) || caseIdx < 0 || caseIdx >= draft.cases.length) {
      onConsumePendingFocusPlanCase?.();
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const el = caseRowRefs.current[caseIdx];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedCaseIdx(caseIdx);
        window.setTimeout(() => setHighlightedCaseIdx(null), 3000);
      }
      onConsumePendingFocusPlanCase?.();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [isOpen, pendingFocusPlanCase, draft.cases.length, onConsumePendingFocusPlanCase]);

  const handleClone = () => {
    if (!selectedPlan) return;
    const baseName = (selectedPlan.name || 'Untitled').trim();
    const clonedName = `${baseName}_${createShortCloneSuffix()}`;
    setIsCloningDraft(true);
    setDraft({
      clid: clinicId,
      name: clonedName,
      description: selectedPlan.description || '',
      cases: Array.isArray(selectedPlan.cases) && selectedPlan.cases.length > 0
        ? selectedPlan.cases.map((c) => ({
          name: c.name || '',
          messages: Array.isArray(c.messages) && c.messages.length > 0 ? [...c.messages] : [''],
          is_skipped: !!c.is_skipped,
          annotation: c.annotation || '',
          preview_pairs: Array.isArray(c.preview_pairs) ? [...c.preview_pairs] : [],
        })).map(normalizeCaseForDraft)
        : [normalizeCaseForDraft(createEmptyCase(generateUniqueCaseCode(clinicId, [])))],
    });
    onSelectPlan(null);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      clid: clinicId,
      name: draft.name,
      description: draft.description,
      cases: draft.cases,
    };
    const result = await onSavePlan(selectedPlanId, payload);
    setSaving(false);
    if (!result?.success) {
      setError(result?.error || 'Failed to save test plan.');
      setSaveSuccess(false);
      return;
    }
    setSaveSuccess(true);
    if (autoCloseOnSave) {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-xl border border-[#2A3942] bg-[#111B21] p-3 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-300 font-semibold uppercase tracking-wide">Test Plan</span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto px-2 py-1 rounded text-xs text-gray-300 bg-[#2A3942] hover:bg-[#3d4a52]"
        >
          Close
        </button>
      </div>

      <div className="flex items-center gap-2">
        <select
          className="bg-[#202C33] text-white text-sm rounded px-2 py-1 border border-[#2A3942] min-w-56"
          value={selectedPlanId || ''}
          onChange={(e) => onSelectPlan(e.target.value || null)}
          disabled={isRunningPlan}
        >
          <option value="">New plan</option>
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>{plan.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52]"
          onClick={() => {
            setIsCloningDraft(false);
            setDraft(createEmptyDraft(clinicId));
            setError(null);
            setSaveSuccess(false);
            onSelectPlan(null);
          }}
          disabled={isRunningPlan}
        >
          New
        </button>
        <button
          type="button"
          className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50"
          onClick={handleClone}
          disabled={isRunningPlan || !selectedPlan}
        >
          Clone
        </button>
        {selectedPlanId && (
          <button
            type="button"
            className="px-3 py-1.5 text-xs rounded bg-red-900 text-red-100 hover:bg-red-800"
            onClick={() => onDeletePlan(selectedPlanId)}
            disabled={isRunningPlan}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          onClick={handleToggleAllExpected}
          disabled={isRunningPlan || draft.cases.length === 0}
          className="px-3 py-1.5 text-xs rounded bg-[#1d4f8c] text-blue-100 hover:bg-[#2465b2] disabled:opacity-50"
        >
          {allExpectedExpanded ? 'Hide all expected' : 'Show all expected'}
        </button>
        <button
          type="button"
          onClick={() =>
            onGenerateExpectedReport?.(buildExpectedReport(draft, selectedPlanId, clinicId))
          }
          disabled={isRunningPlan || draft.cases.length === 0}
          className="px-3 py-1.5 text-xs rounded bg-[#1d4f8c] text-blue-100 hover:bg-[#2465b2] disabled:opacity-50"
          title="Build a report from this plan using each message's expected answer (no agent run)."
        >
          Generate report
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => {
            setDraft((prev) => ({ ...prev, name: e.target.value }));
            setSaveSuccess(false);
          }}
          placeholder="Plan name"
          className="px-3 py-2 rounded bg-[#202C33] text-white text-sm border border-[#2A3942]"
          disabled={isRunningPlan}
        />
        <input
          type="text"
          value={draft.description}
          onChange={(e) => {
            setDraft((prev) => ({ ...prev, description: e.target.value }));
            setSaveSuccess(false);
          }}
          placeholder="Description (optional)"
          className="px-3 py-2 rounded bg-[#202C33] text-white text-sm border border-[#2A3942]"
          disabled={isRunningPlan}
        />
      </div>

      <div className="space-y-2 max-h-[62vh] overflow-y-auto pr-1">
        {draft.cases.map((caseItem, caseIdx) => (
          <div
            key={caseIdx}
            ref={(el) => {
              if (el) caseRowRefs.current[caseIdx] = el;
              else delete caseRowRefs.current[caseIdx];
            }}
            className={`p-2 rounded border space-y-2 transition ${
              highlightedCaseIdx === caseIdx
                ? 'border-[#00A884] ring-2 ring-[#00A884]/40 bg-[#1a2a24]'
                : 'border-[#2A3942] bg-[#202C33]'
            }`}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={caseItem.name}
                onChange={(e) => {
                  updateCase(caseIdx, (c) => ({ ...c, name: e.target.value }));
                  setSaveSuccess(false);
                }}
                placeholder={`Case ${caseIdx + 1} name`}
                className="flex-1 px-2 py-1 rounded bg-[#111B21] text-white text-xs border border-[#2A3942]"
                disabled={isRunningPlan}
              />
              <label className="flex items-center gap-1 text-xs text-gray-300 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!caseItem.is_skipped}
                  onChange={(e) => {
                    updateCase(caseIdx, (c) => ({ ...c, is_skipped: e.target.checked }));
                    setSaveSuccess(false);
                  }}
                  disabled={isRunningPlan}
                  className="accent-[#00A884]"
                />
                Skipped
              </label>
              <button
                type="button"
                onClick={() => setExpectedResultExpanded((prev) => ({ ...prev, [caseIdx]: !prev[caseIdx] }))}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52]"
                title={expectedResultExpanded[caseIdx] ? 'Hide expected result' : 'Show expected result'}
              >
                <span aria-hidden>{expectedResultExpanded[caseIdx] ? '▾' : '▸'}</span>
                {expectedResultExpanded[caseIdx] ? 'Hide expected' : 'Show expected'}
              </button>
              <button
                type="button"
                onClick={() => setDraft((prev) => ({ ...prev, cases: prev.cases.filter((_, idx) => idx !== caseIdx) }))}
                disabled={isRunningPlan || draft.cases.length <= 1}
                className="px-2 py-1 text-xs rounded bg-red-900 text-red-100 disabled:opacity-50"
              >
                Remove
              </button>
            </div>
            <div className="space-y-0.5">
              <input
                type="text"
                value={String(caseItem.annotation ?? '')}
                onChange={(e) => {
                  updateCase(caseIdx, (c) => ({ ...c, annotation: e.target.value }));
                  setSaveSuccess(false);
                }}
                placeholder="Annotation (optional)"
                aria-label="Annotation (optional)"
                className="w-full px-2 py-1.5 rounded bg-[#111B21] text-orange-200 text-xs border border-orange-800/50 placeholder:text-orange-400/55 focus:outline-none focus:ring-1 focus:ring-orange-500/60"
                disabled={isRunningPlan}
              />
            </div>
            {caseItem.messages.map((msg, msgIdx) => (
              <div key={msgIdx} className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveCaseMessage(caseIdx, msgIdx, -1)}
                      disabled={isRunningPlan || msgIdx === 0}
                      className="px-1.5 py-0.5 text-[10px] leading-none rounded bg-[#2A3942] text-white disabled:opacity-40"
                      title="Move message up"
                      aria-label={`Move message ${msgIdx + 1} up`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCaseMessage(caseIdx, msgIdx, 1)}
                      disabled={isRunningPlan || msgIdx >= caseItem.messages.length - 1}
                      className="px-1.5 py-0.5 text-[10px] leading-none rounded bg-[#2A3942] text-white disabled:opacity-40"
                      title="Move message down"
                      aria-label={`Move message ${msgIdx + 1} down`}
                    >
                      ↓
                    </button>
                  </div>
                  <input
                    type="text"
                    value={msg}
                    onChange={(e) => {
                      const nextMessage = e.target.value;
                      updateCase(caseIdx, (c) => ({
                        ...c,
                        messages: c.messages.map((m, idx) => (idx === msgIdx ? nextMessage : m)),
                        preview_pairs: buildPreviewPairsFromMessages(
                          c.messages.map((m, idx) => (idx === msgIdx ? nextMessage : m)),
                          c.preview_pairs || []
                        ),
                      }));
                      setSaveSuccess(false);
                    }}
                    placeholder={`Message ${msgIdx + 1}`}
                    className="flex-1 px-2 py-1 rounded bg-[#111B21] text-white text-xs border border-[#2A3942]"
                    disabled={isRunningPlan}
                  />
                  <button
                    type="button"
                    onClick={() => updateCase(caseIdx, (c) => {
                      const nextMessages = c.messages.filter((_, idx) => idx !== msgIdx);
                      return {
                        ...c,
                        messages: nextMessages,
                        preview_pairs: buildPreviewPairsFromMessages(nextMessages, c.preview_pairs || []),
                      };
                    })}
                    disabled={isRunningPlan || caseItem.messages.length <= 1}
                    className="px-2 py-1 text-xs rounded bg-[#2A3942] text-white disabled:opacity-50"
                  >
                    -
                  </button>
                </div>
                {expectedResultExpanded[caseIdx] && (
                  <input
                    type="text"
                    value={String(caseItem.preview_pairs?.[msgIdx]?.expected_result || '')}
                    onChange={(e) => {
                      const nextExpected = e.target.value;
                      updateCase(caseIdx, (c) => {
                        const nextPairs = buildPreviewPairsFromMessages(c.messages, c.preview_pairs || []);
                        nextPairs[msgIdx] = {
                          ...(nextPairs[msgIdx] || { client_message: String(c.messages[msgIdx] || ''), expected_result: '' }),
                          expected_result: nextExpected,
                        };
                        return {
                          ...c,
                          preview_pairs: nextPairs,
                        };
                      });
                      setSaveSuccess(false);
                    }}
                    placeholder={`Expected result ${msgIdx + 1}`}
                    className="w-full px-2 py-1 rounded bg-[#111B21] text-[#53bdeb] placeholder:text-blue-300 text-xs border border-[#2A3942]"
                    disabled={isRunningPlan}
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => updateCase(caseIdx, (c) => {
                const nextMessages = [...c.messages, ''];
                return {
                  ...c,
                  messages: nextMessages,
                  preview_pairs: buildPreviewPairsFromMessages(nextMessages, c.preview_pairs || []),
                };
              })}
              disabled={isRunningPlan}
              className="px-2 py-1 text-xs rounded bg-[#2A3942] text-white"
            >
              + Add message
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setDraft((prev) => ({
            ...prev,
            cases: [...prev.cases, normalizeCaseForDraft(createEmptyCase(generateUniqueCaseCode(clinicId, prev.cases)))],
          }))}
          disabled={isRunningPlan}
          className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white"
        >
          + Add case
        </button>
        <input
          ref={importFileInputRef}
          type="file"
          accept=".json,.csv,application/json,text/csv"
          style={{ display: 'none' }}
          onChange={handleImportFileChange}
        />
        <button
          type="button"
          onClick={handleImportFromJsonClick}
          disabled={isRunningPlan}
          title={'Import JSON or a Conversations export CSV. JSON: array of string arrays or objects with messages/name/annotation. CSV: header conversation,user message,ai message — rows with the same conversation id become one multi-turn case with expected results from the ai message column.'}
          className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50"
        >
          Import from JSON/CSV
        </button>
        <button
          type="button"
          onClick={() => setJsonEditOpen((prev) => !prev)}
          disabled={isRunningPlan}
          className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50"
          title="Export, edit, or apply cases as JSON (same shape as Import from JSON)."
        >
          {jsonEditOpen ? 'Hide JSON' : 'Edit JSON'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || isRunningPlan}
          className="px-3 py-1.5 text-xs rounded bg-[#00A884] text-white disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save plan'}
        </button>
        {saveSuccess && (
          <span className="text-xs text-emerald-300">Saved</span>
        )}
        {importStatus && (
          <span className="text-xs text-emerald-300">{importStatus}</span>
        )}
        {jsonEditStatus && (
          <span className="text-xs text-emerald-300">{jsonEditStatus}</span>
        )}
        {isRunningPlan && (
          <button
            type="button"
            onClick={onStopRun}
            className="px-3 py-1.5 text-xs rounded bg-red-900 text-red-100"
          >
            Stop
          </button>
        )}
        {runProgressText && <span className="text-xs text-gray-300">{runProgressText}</span>}
      </div>
      {jsonEditOpen && (
        <div className="space-y-2 rounded border border-[#2A3942] bg-[#202C33] p-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportCasesJson}
              disabled={isRunningPlan || draft.cases.length === 0}
              className="px-3 py-1.5 text-xs rounded bg-[#1d4f8c] text-blue-100 hover:bg-[#2465b2] disabled:opacity-50"
            >
              Export JSON
            </button>
            <input
              ref={jsonEditFileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleJsonEditUpload}
            />
            <button
              type="button"
              onClick={() => jsonEditFileInputRef.current?.click()}
              disabled={isRunningPlan}
              className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50"
            >
              Upload JSON
            </button>
            <button
              type="button"
              onClick={handleApplyJsonToDraft}
              disabled={isRunningPlan || !String(jsonEditText || '').trim()}
              className="px-3 py-1.5 text-xs rounded bg-[#00A884] text-white hover:bg-[#02956f] disabled:opacity-50"
              title="Replace draft cases with the JSON below. Click Save plan to persist."
            >
              Apply to draft
            </button>
            <span className="text-[11px] text-gray-400">
              Same array shape as Import from JSON. Apply replaces draft cases; Save plan persists.
            </span>
          </div>
          <textarea
            value={jsonEditText}
            onChange={(e) => {
              setJsonEditText(e.target.value);
              setSaveSuccess(false);
            }}
            placeholder='[{"name":"CASE.0001","messages":["hello"],"annotation":"","is_skipped":false,"expected_results":[""]}]'
            rows={12}
            disabled={isRunningPlan}
            className="w-full font-mono text-xs p-2 rounded bg-[#111B21] text-white border border-[#2A3942] placeholder:text-gray-500"
          />
        </div>
      )}
      {error && <div className="text-xs text-red-300">{error}</div>}
      </div>
    </div>
  );
};

export default ApiTestPlanPanel;
